<script setup>
  import { getQuery } from '@/utils/url'
  import { notify } from 'notiwind'
  import { computed } from 'vue'

  const url = useRequestURL()
  const { path, query } = useRoute()

  const props = defineProps({
    isCareerSite: { default: false, type: Boolean },
    color: { default: '#58d9db', type: String },
    job: { required: true, type: Object },
    hasApplied: { default: false, type: Boolean }
  })

  const emit = defineEmits(['apply'])

  const experienceLabel = computed(() => {
    if (!props.job.experience) return
    switch (props.job.experience) {
      case 'beginner':
        return 'débutant (moins d’un an)'
      case 'junior':
        return 'entre 2 et 5 ans'
      case 'confirmed':
        return 'entre 5 et 7 ans'
      default:
        return 'entre 7 et 10 ans'
    }
  })

  function getCities(job) {
    const cities = job.locations.map(loc => loc.city)
    return [...new Set(cities)]
  }

  function onCopy(e) {
    notify({ group: 'main', type: 'success', title: "URL de l'offre copiée" })
  }
</script>

<template>
  <section
    :class="[
      'sticky z-10  max-w-full bg-zinc-100 lg:!z-0 lg:w-80',
      isCareerSite ? 'lg:top-42 max-lg:top-0 lg:fixed' : 'max-lg:top-16  lg:sticky lg:top-[242px] lg:z-0'
    ]"
  >
    <div class="top-0 w-full p-4 pb-2 sm:px-8 sm:py-6 sm:pb-4 lg:h-[calc(100vh-160px)] lg:min-h-full lg:p-8" ref="sticky">
      <div
        :class="[
          'z-10 flex h-full max-lg:flex-wrap max-lg:items-center max-lg:justify-evenly  lg:flex-col lg:justify-between lg:space-y-8',
          !isCareerSite && 'lg:fixed lg:top-[282px]'
        ]"
      >
        <NuxtLink
          class="flex text-left max-lg:basis-1/4 max-sm:text-sm md:space-x-4 lg:order-1 lg:-mt-2 lg:self-start"
          :to="`${path.replace('/' + job.slug, '')}${getQuery(query)}`"
        >
          <Icon name="arrow-turn-down-left" size="xl" type="regular" :style="{ color }" />
          <p class="job-link !hidden underline decoration-2 underline-offset-2 md:!block">Toutes les offres</p>
        </NuxtLink>

        <div class="max-lg:basis-1/2 max-lg:text-center lg:order-3 lg:self-end" v-if="job.isStatus('active') && !job.isExpired()">
          <JobApplyButton :job="job" :color="color" :hasApplied="hasApplied" :isCareerSite="isCareerSite" @apply="$emit('apply')" />
        </div>

        <div
          :class="[
            'z-20 flex items-end justify-end max-lg:basis-1/4  lg:order-4 lg:flex-1',
            isCareerSite ? 'lg:bottom-8 lg:z-0 lg:!mt-0' : 'lg:items-start'
          ]"
        >
          <div class="space-x-2">
            <Tooltip content="Partager sur LinkedIn">
              <NuxtLink class="duration-300 hover:text-linkedin" target="_blank" :to="`https://www.linkedin.com/sharing/share-offsite/?url=${url}`">
                <Icon fixedWidth name="linkedin-in" size="lg" type="brands" />
              </NuxtLink>
            </Tooltip>
            <Tooltip content="Copier l'URL">
              <button class="ml-2 duration-300 max-sm:text-xs" v-clipboard:copy="url" v-clipboard:success="onCopy">
                <Icon class="icon-link" fixedWidth name="link" size="lg" type="solid" />
              </button>
            </Tooltip>
          </div>
        </div>

        <div class="mt-4 w-full lg:order-2">
          <ul class="text-sm max-lg:-mx-2 max-lg:flex max-lg:flex-wrap max-lg:justify-between xs:text-base sm:text-lg lg:space-y-3">
            <li class="capitalize max-lg:px-2 max-lg:py-1" v-if="job.locations?.length">
              <Tooltip :content="job.locations.map(p => [p.street, p.postalCode, p.city].filter(Boolean).join(', ')).join(' / ')" placement="bottom">
                <span class="underline">{{ getCities(job).join(', ') }}</span>
              </Tooltip>
            </li>

            <li class="max-lg:px-2 max-lg:py-1" v-if="job.type">
              <JobType :job="job" />
            </li>

            <li class="max-lg:px-2 max-lg:py-1">
              {{ job.partTime ? 'Temps partiel' : 'Temps complet' }}
            </li>

            <li class="max-lg:px-2 max-lg:py-1" v-if="job.categories?.length">
              Catégorie<template v-if="job.categories.length > 1">s</template>
              {{
                job.categories
                  .map(c => c.name)
                  .sort()
                  .join(', ')
              }}
            </li>

            <li class="max-lg:px-2 max-lg:py-1" v-if="experienceLabel">
              Expérience requise :<br class="!hidden lg:!block" />
              {{ experienceLabel }}
            </li>

            <li class="max-lg:px-2 max-lg:py-1">
              <div>
                <span v-if="job.salaryMin === null && (job.salaryMax === 0 || job.salaryMax === null)"> Rémunération selon profil</span>
                <span v-else-if="job.salaryMin !== null && job.salaryMax === null">
                  À partir de {{ job.salaryMin }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par
                  {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
                </span>
                <span v-else-if="job.salaryMin === job.salaryMax">
                  {{ job.salaryMin }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
                </span>
                <span v-else>
                  Entre {{ job.salaryMin }}€ et {{ job.salaryMax }}€ {{ job.salaryType ? job.salaryType : 'brut' }} par
                  {{ job.salaryPeriod === 'month' ? 'mois' : 'an' }}
                </span>
              </div>
            </li>

            <li class="max-lg:px-2 max-lg:py-1" v-if="job.teleworkable">
              <div>Télétravail possible</div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
  .fa-circle-info,
  .icon-link:hover,
  .previous-link:hover {
    color: v-bind('color');
  }

  .popper > div {
    color: inherit;
  }
</style>
